import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FeatureTypeService, NotificationsService, ProjectService} from '@catcore/services';
import {TreeNode} from 'primeng/primeng';
import {Feature} from "@catcore/services/feature/feature";
import {Observable, Subject, Subscription} from "rxjs";
import {ProjectFeatureService} from "@catmodels/project-feature/services/project-feature.service";
import {ProjectFeature} from "@catmodels/project-feature/types";
import {Project} from "@catcore/types/api-v2";
import {GenericLinkedResource} from "@catcore/types/base";
import {DisplayTypes} from "@catui/components/panel/types";
import * as shape from 'd3-shape';
import {PlacementTypes} from "@swimlane/ngx-charts/release/common/tooltip/position";
import {StyleTypes} from "@swimlane/ngx-charts/release/common/tooltip/style.type";
import {StyleType} from "@catui/types";
import {GraphComponent} from "@swimlane/ngx-graph/release/graph/graph.component";

/**
 * A component
 */
@Component({
    templateUrl: './feature-project-usages.component.html',
    styleUrls: ['feature-project-usages.component.scss']
})
export class FeatureProjectUsagesComponent implements OnInit, OnDestroy, AfterViewInit {
  feature: Feature;
  featureSubscription: Subscription;
  projectFeatureSubscription: Subscription;
  treeNodes = new Array<TreeNode>();
  hierarchicalGraph = { nodes: [], links: [] };
  curve = shape.curveLinear;
  panelDisplayType = DisplayTypes;
  placementTypes = PlacementTypes;
  tooltipStyleTypes = StyleTypes;
  styleTypes = StyleType;
  @ViewChild('graph') graph: GraphComponent;
  view: any;

  constructor(private featureService: FeatureTypeService,
              private projectFeatureService: ProjectFeatureService,
              private projectService: ProjectService,
              private notificationsService: NotificationsService) {
  }

  ngOnInit() {
    this.featureSubscription = this.featureService.getCurrent().subscribe((feature: Feature) => {
      if (feature) {
        this.feature = feature;
        this.projectFeatureSubscription = this.projectFeatureService.getAll({type: feature.id})
          .subscribe((projectFeatures: ProjectFeature[]) => {
            this.makeTreeNodes(projectFeatures);
            this.updateGraph();
          });
      }
    });
  }

  ngAfterViewInit() {
    this.updateGraph();
  }

  ngOnDestroy() {
    this.featureSubscription.unsubscribe();
    this.projectFeatureSubscription.unsubscribe();
  }

  private makeTreeNodes(projectFeatures: ProjectFeature[]) {
    this.treeNodes = [];
    this.hierarchicalGraph = { nodes: [], links: [] };

    const projectNodes = new Map<string, TreeNode>();
    const projectsToGuiZoneNodes = new Map<string, Map<string, TreeNode>>();
    projectFeatures.forEach((projectFeature: ProjectFeature) => {
      const projectUri = projectFeature._embedded.gui_zone._links.project.href;
      // Create Project node if not already present
      if (!projectNodes.has(projectUri)) {
        projectNodes.set(projectUri, {
          type: 'project',
          data: projectFeature._embedded.gui_zone._links.project,
          children: [],
          expanded: true,
          styleClass: 'node-project'
        });
        projectsToGuiZoneNodes.set(projectUri, new Map<string, TreeNode>());
        // Create graph nodes
        this.hierarchicalGraph.nodes.push({
          id: this.normKey(projectUri),
          label: projectFeature._embedded.gui_zone._links.project.label
        });
      }
      const guiZoneNodes = projectsToGuiZoneNodes.get(projectUri);
      const guiZoneUri = projectFeature._links.gui_zone.href;
      // Create GuiZone node if not already present
      if (!guiZoneNodes.has(guiZoneUri)) {
        guiZoneNodes.set(guiZoneUri, {
          type: 'zone',
          data: projectFeature._links.gui_zone,
          children: [],
          expanded: true,
          styleClass: 'node-zone'
        });
        // Create graph nodes
        this.hierarchicalGraph.nodes.push({
          id: this.normKey(guiZoneUri),
          label: projectFeature._links.gui_zone.label
        });
        this.hierarchicalGraph.links.push({
          id: this.normKey(projectUri + guiZoneUri),
          source: this.normKey(projectUri),
          target: this.normKey(guiZoneUri)
        });
      }
      // Add Feature to children of GuiZone node
      guiZoneNodes.get(guiZoneUri).children.push({
        type: 'feature',
        leaf: true,
        data: {
          label: projectFeature.name,
          ...projectFeature
        },
        styleClass: 'node-feature'
      });
      // Create graph data
      this.hierarchicalGraph.nodes.push({
        id: this.normKey(projectFeature._links.self.href),
        label: projectFeature.name
      });
      this.hierarchicalGraph.links.push({
        id: this.normKey(guiZoneUri + projectFeature._links.self.href),
        source: this.normKey(guiZoneUri),
        target: this.normKey(projectFeature._links.self.href)
      });
    });
    projectNodes.forEach((projectNode, uri) => {
      projectNode.children = Array.from(projectsToGuiZoneNodes.get(uri).values());
      this.treeNodes.push(projectNode);
    });
  }


  private normKey(key: string): string {
    return key.replace(/[^\w]*/g, '');
  }

  updateAllProjects() {
    let mergedObservable: Observable<any>;
    this.treeNodes.forEach((projectNode) => {
      if (mergedObservable)
        mergedObservable.merge(this.updateProject(projectNode.data).asObservable());
      else
        mergedObservable = this.updateProject(projectNode.data).asObservable();
    });
    let errors = this.treeNodes.length;
    mergedObservable.subscribe(
      (value) =>  errors--,
      (error) =>  this.notificationsService.error(errors + ' of ' + this.treeNodes.length + ' updates failed!', ''),
      () => this.notificationsService.success('Update successful!', '')
    );
  }

  updateProjectFeature(feature: ProjectFeature) {
    this.projectFeatureService.updateFromFeature(feature.id).subscribe(value => this.notificationsService.success('Feature updated!', ''));
  }

  updateProject(project: GenericLinkedResource): Subject<any> {
      return this.projectService.updateAllFeatures(
        { id: Number.parseInt(project.href.split('/').pop()) } as Project,
        this.feature.id
      );
  }

  cNode(row) {
    return row as TreeNode;
  }

  getNodeColor(node: any) {
    if (node.id.includes('projects')) {
      return '#fac8e1';
    } else if (node.id.includes('zone')) {
      return '#d9c8fa';
    } else if (node.id.includes('feature')) {
      return '#c8d9fa';
    }
    return false;
  }

  updateGraph() {
    if (this.graph)
      this.graph.update();
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/features/components/project-usages/feature-project-usages.component.ts